import { trigger, transition, style, animate, state } from '@angular/animations';
import { Component, ElementRef, NgZone, OnInit, ViewChild, ChangeDetectorRef } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Capacitor } from '@capacitor/core';
import { Platform } from '@ionic/angular';
import { UserNotificationContactRequest } from 'node_js/models/business/user_notification_settings/user-notification-contact-request';
import { SnackBarColor } from 'node_js/models/enums/snackbar-color';
import { UserContact } from 'node_js/models/tables/user_contact';
import { UserNotification } from 'node_js/models/tables/user_notification';
import { BehaviorSubject } from 'rxjs';
import { ContactInfoComponent } from 'src/app/components/contact-info/contact-info.component';
import { ContactsComponent, ContactsMode, SelectedContact } from 'src/app/components/contacts/contacts.component';
import { GadgetComponentComponent } from 'src/app/components/gadget-component/gadget-component.component';
import { GadgetComponent } from 'src/app/components/gadget/gadget.component';
import { LabService } from 'src/app/services/lab.service';
import { LiveUpdatesService } from 'src/app/services/live-updates.service';
import { StateService } from 'src/app/services/state.service';
import { UserAuthService } from 'src/app/services/user-auth.service';
import { presentLoading, presentSnackBar, transitionSpeed } from 'src/global';
import Swiper from 'swiper';
import { AppComponent } from 'src/app/app.component';
import { LabComponent } from 'src/app/components/lab/lab.component';
import { AndroidBackPriority } from 'node_js/models/enums/android-back-priority';
import { Router } from '@angular/router';


//The numbers have to be the order that the slides are in, in the html
export enum MainSliderSection {
  LABS = 0,
  RESOURCES = 1,
  CONTACTS = 2,
  CONTACTINFO = 3,
  CODEBL = 4,
  LAB = 5,
  GADGET = 6,
  COMPONENT = 7,
  CONTROLLER = 8,
  LABSETTINGS = 9,
  LABUSERS = 10,

}



@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.page.html',
  styleUrls: ['./dashboard.page.scss'],
  animations: [
    trigger('slideNav', [
      state('hidden', style({ width: '0px' })),
      state('visible', style({ width: '300px' })),
      transition('hidden <=> visible', [
        animate('400ms ease-in-out')
      ])
    ])
  ],

})
export class DashboardPage implements OnInit {

  transitioningSlideIndex: number = null;

  capacitorPlatform: string = "";

  //Enums
  MainSliderSection = MainSliderSection;
  ContactsMode = ContactsMode;

  //Swiper
  selectedMainSliderSection: MainSliderSection;
  @ViewChild("mainSwiper") swiperElementRef: ElementRef;
  swiper: Swiper;

  //Components we are rendering
  forceMainRenders = false;

  enableSideNav = false;

  //Is nav hidden
  hideNav = false;

  //Disable nav animation on load
  navAnimationDisabled = true;

  changingSlide: boolean = false;

  forceTransitionBackToContacts = false;

  currentlyManuallySliding = false;

  minMainSlide = MainSliderSection.LABS;
  maxMainSlide = MainSliderSection.RESOURCES;

  //This will allow the child components to confirm if we can leave a slide or not (such as nav click)
  confirmSlideChangeCallback: () => Promise<boolean>;

  private contactsComponent: ContactsComponent;
  @ViewChild('contactsComponent') set contacts(content: ContactsComponent) {
    if (content) { // initially setter gets called with undefined
      this.contactsComponent = content;
    }
  }

  private contactInfoComponent: ContactInfoComponent;
  @ViewChild('contactInfoComponent') set contactInfo(content: ContactInfoComponent) {
    if (content) { // initially setter gets called with undefined
      this.contactInfoComponent = content;
    }
  }

  @ViewChild('gadgetComponent') gadgetComponent: GadgetComponent;
  @ViewChild('gadgetComponentComponent', { static: true }) set gadgetComponentComponent(content: GadgetComponentComponent) {
    if (content) { // initially setter gets called with undefined
      console.log('[Dashboard] gadgetComponentComponent ViewChild setter called with:', content);
      this._gadgetComponentComponent = content;
    }
  }
  private _gadgetComponentComponent: GadgetComponentComponent;
  get gadgetComponentComponent(): GadgetComponentComponent {
    return this._gadgetComponentComponent;
  }
  @ViewChild('labComponent') labComponent: LabComponent;
  @ViewChild('dashboardSidenav') dashboardSidenav: any;

  private _initialRender = false;

  get initialRender(): boolean {
    return this._initialRender;
  }

  set initialRender(value: boolean) {
    console.log(`[Dashboard] initialRender changing from ${this._initialRender} to ${value}`);
    this._initialRender = value;
  }

  userNotificationContactRequest: UserNotificationContactRequest | null = null;

  // Back button subscription
  private backButtonSub: any;

  // Interval for periodic access check
  private accessCheckInterval: any;

  constructor(private matSnackBar: MatSnackBar, private ngZone: NgZone, protected labService: LabService,
    private matDialog: MatDialog, private userAuthService: UserAuthService, private platform: Platform, private stateService: StateService,
    private appComponent: AppComponent, private cdr: ChangeDetectorRef, private router: Router
  ) {
    this.stateService.getMainSliderSectionObservable().subscribe((mainSliderSection: MainSliderSection) => {
      this.changeSlide(mainSliderSection, true);
    })

    // Subscribe to lab user removal events to check access and navigate back to labs if needed
    this.labService.currentSelectedLabUserRemoved.subscribe((data: { labId: string, message: string }) => {
      console.log('[Dashboard] Received currentSelectedLabUserRemoved event:', data);

      // Check if we're currently viewing a lab, gadget, or component related to this lab
      if (this.selectedMainSliderSection === MainSliderSection.LAB ||
        this.selectedMainSliderSection === MainSliderSection.GADGET ||
        this.selectedMainSliderSection === MainSliderSection.COMPONENT ||
        this.selectedMainSliderSection === MainSliderSection.CONTROLLER ||
        this.selectedMainSliderSection === MainSliderSection.LABSETTINGS ||
        this.selectedMainSliderSection === MainSliderSection.LABUSERS) {

        // Check if the current selected lab matches the removed lab
        if (this.labService.currentSelectedLab && this.labService.currentSelectedLab.lab.id === data.labId) {
          console.log('[Dashboard] Current user was removed from selected lab, navigating back to labs');

          // Navigate back to labs
          this.changeSlide(MainSliderSection.LABS, true);

          // Show snackbar message
          setTimeout(() => {
            presentSnackBar(data.message, SnackBarColor.DANGER, this.matSnackBar);
          }, 100);
        }
      }
    })
  }


  async ngOnInit() {
    console.log('[Dashboard] === ngOnInit called ===');
    console.log('[Dashboard] Initial selectedMainSliderSection:', this.selectedMainSliderSection);
    console.log('[Dashboard] Min slide:', this.minMainSlide);
    console.log('[Dashboard] Max slide:', this.maxMainSlide);

    // Subscribe to app reload events
    this.appComponent.reloadedApp$.subscribe(() => {
      this.onReloadedApp();
    });



    // Set up back button handler
    this.handleBackButton();

    this.capacitorPlatform = await Capacitor.getPlatform();

    // Optimize for mobile devices
    if (this.capacitorPlatform === 'ios' || this.capacitorPlatform === 'android') {
      // Reduce transition speed for mobile by using a local variable
      const mobileTransitionSpeed = 200;
      // Note: We can't modify the global transitionSpeed, but we can optimize other aspects

      // iOS-specific: add CSS optimizations to prevent visual glitches
      if (this.capacitorPlatform === 'ios') {
        this.addIOSOptimizations();
      }
    }

    /*
    setTimeout(() => {
      this.initialRender = false;
    }, 1000); // Preload all main components for 1 second
    */
  }

  /**
   * Ionic lifecycle hook - called when the view is about to become active
   */
  ionViewWillEnter() {
    console.log('[Dashboard] === ionViewWillEnter called ===');
  }

  /**
   * Ionic lifecycle hook - called when the view becomes active
   * This ensures the back button handler is set up when navigating to this page
   */
  ionViewDidEnter() {
    console.log('[Dashboard] === ionViewDidEnter called ===');
    console.log('[Dashboard] Setting up back button handler...');

    // Set up the back button handler when the view becomes active
    // Only call it once - the method will handle duplicate subscription prevention
    this.handleBackButton();

    // Also try to set up a global back button listener to see if it's being detected
    this.setupGlobalBackButtonListener();

    // Also try Capacitor App plugin as a fallback
    this.tryCapacitorAppBackButton();

    // Check if the current user still has access to the selected lab
    this.checkUserAccessToCurrentLab();

    // Set up periodic access check when viewing lab-related content
    this.setupPeriodicAccessCheck();
  }

  /**
   * Set up periodic checking of user access to the current lab
   */
  private setupPeriodicAccessCheck() {
    // Clear any existing interval
    if (this.accessCheckInterval) {
      clearInterval(this.accessCheckInterval);
    }

    // Set up periodic check every 5 seconds when viewing lab-related content
    this.accessCheckInterval = setInterval(() => {
      if (this.selectedMainSliderSection === MainSliderSection.LAB ||
        this.selectedMainSliderSection === MainSliderSection.GADGET ||
        this.selectedMainSliderSection === MainSliderSection.COMPONENT ||
        this.selectedMainSliderSection === MainSliderSection.CONTROLLER ||
        this.selectedMainSliderSection === MainSliderSection.LABSETTINGS ||
        this.selectedMainSliderSection === MainSliderSection.LABUSERS) {
        this.checkUserAccessToCurrentLab();
      }
    }, 5000);
  }

  /**
   * Check if the current user still has access to the currently selected lab
   */
  private checkUserAccessToCurrentLab() {
    if (!this.labService.currentSelectedLab || !this.userAuthService.currentUserComplete) {
      return;
    }

    const currentUserId = this.userAuthService.currentUserComplete.user.id;
    const labComplete = this.labService.currentSelectedLab;

    // Check if the current user exists in this lab
    const userHasAccess = labComplete.labUsersComplete.some(
      labUser => labUser.labUser.userId === currentUserId
    );

    if (!userHasAccess) {
      console.log('[Dashboard] Current user no longer has access to selected lab, navigating back to labs');

      // Navigate back to labs
      this.changeSlide(MainSliderSection.LABS, true);

      // Show snackbar message
      setTimeout(() => {
        presentSnackBar('You were removed from this lab', SnackBarColor.DANGER, this.matSnackBar);
      }, 100);
    }
  }

  /**
   * Ionic lifecycle hook - called when the view is about to leave
   */
  ionViewWillLeave() {
    console.log('[Dashboard] === ionViewWillLeave called ===');

    // Clear periodic access check interval
    if (this.accessCheckInterval) {
      clearInterval(this.accessCheckInterval);
      this.accessCheckInterval = null;
    }
  }

  /**
   * Ionic lifecycle hook - called when the view has left
   */
  ionViewDidLeave() {
    console.log('[Dashboard] === ionViewDidLeave called ===');

    // Clean up back button subscription
    if (this.backButtonSub) {
      console.log('[Dashboard] Cleaning up back button subscription');
      this.backButtonSub.unsubscribe();
      this.backButtonSub = null;
    }

    // Clear periodic access check interval
    if (this.accessCheckInterval) {
      clearInterval(this.accessCheckInterval);
      this.accessCheckInterval = null;
    }
  }

  /**
   * Clean up back button subscription
   */
  ngOnDestroy() {
    console.log('[Dashboard] === ngOnDestroy called ===');

    // Clean up back button subscription
    if (this.backButtonSub) {
      console.log('[Dashboard] Cleaning up back button subscription in ngOnDestroy');
      this.backButtonSub.unsubscribe();
      this.backButtonSub = null;
    }

    // Clear periodic access check interval
    if (this.accessCheckInterval) {
      clearInterval(this.accessCheckInterval);
      this.accessCheckInterval = null;
    }
  }

  /**
   * Called when the app has reloaded user data and reconnected sockets after resume.
   * Add your dashboard refresh logic here.
   */
  onReloadedApp() {
    console.log('[Dashboard] App reloaded — forcing change detection');
    this.cdr.detectChanges();
  }

  /**
   * Debug method to test back button functionality and platform status
   */
  debugBackButton() {
    console.log('[Dashboard] === Debug Back Button ===');
    console.log('[Dashboard] Current slide:', this.selectedMainSliderSection);
    console.log('[Dashboard] Platform available:', !!this.platform);
    console.log('[Dashboard] Platform.backButton available:', !!this.platform?.backButton);
    console.log('[Dashboard] Capacitor.isNativePlatform():', Capacitor.isNativePlatform());

    // Test platform service functionality
    if (this.platform) {
      console.log('[Dashboard] Platform service type:', typeof this.platform);
      console.log('[Dashboard] Platform service constructor:', this.platform.constructor.name);

      // Check available methods
      try {
        const methods = Object.getOwnPropertyNames(Object.getPrototypeOf(this.platform));
        console.log('[Dashboard] Available platform methods:', methods);
      } catch (err) {
        console.error('[Dashboard] Error getting platform methods:', err);
      }
    }

    // Check back button subscription status
    if (this.backButtonSub) {
      console.log('[Dashboard] ✅ Back button subscription exists');
      console.log('[Dashboard] Subscription details:', this.backButtonSub);

      // Try to check if the subscription is active
      try {
        if (this.backButtonSub.closed !== undefined) {
          console.log('[Dashboard] Subscription closed status:', this.backButtonSub.closed);
        }
      } catch (err) {
        console.log('[Dashboard] Could not check subscription status:', err);
      }
    } else {
      console.log('[Dashboard] ❌ No back button subscription found');
    }

    // Force back button setup if none exists
    if (!this.backButtonSub) {
      console.log('[Dashboard] Forcing back button setup...');
      this.handleBackButton();
    }

    // Test back button functionality
    this.testBackButtonFunctionality();
  }

  /**
   * Test back button functionality by simulating different slide states
   */
  private testBackButtonFunctionality() {
    console.log('[Dashboard] === Testing Back Button Functionality ===');

    // Test with different slide states
    const testSlides = [MainSliderSection.CONTACTS, MainSliderSection.LAB, MainSliderSection.GADGET];

    testSlides.forEach(slide => {
      console.log(`[Dashboard] Testing back button from slide ${slide}:`);
      console.log(`[Dashboard] - Current slide: ${this.selectedMainSliderSection}`);
      console.log(`[Dashboard] - Min slide: ${this.minMainSlide}`);
      console.log(`[Dashboard] - Would go to: ${slide > this.minMainSlide ? slide - 1 : 'first slide'}`);
    });

    // Simulate back button press logic
    if (this.selectedMainSliderSection > this.minMainSlide) {
      const previousSlide = this.selectedMainSliderSection - 1;
      console.log(`[Dashboard] Back button would navigate from ${this.selectedMainSliderSection} to ${previousSlide}`);
    } else {
      console.log('[Dashboard] Back button would be on first slide - could exit app');
    }
  }

  /**
   * Manually trigger back button behavior for testing
   */
  manualBackButton() {
    console.log('[Dashboard] === Manual Back Button Triggered ===');
    console.log('[Dashboard] Current slide:', this.selectedMainSliderSection);

    // Simulate the same logic as the actual back button handler
    if (this.selectedMainSliderSection > this.minMainSlide) {
      const previousSlide = this.selectedMainSliderSection - 1;
      console.log('[Dashboard] Manually going back from slide', this.selectedMainSliderSection, 'to slide', previousSlide);
      this.changeSlide(previousSlide, true);
    } else if (this.selectedMainSliderSection === this.minMainSlide) {
      console.log('[Dashboard] On first slide, manual back button triggered');
      // You could implement app exit logic here if needed
    } else {
      console.log('[Dashboard] Unexpected slide state for manual back button:', this.selectedMainSliderSection);
    }
  }

  /**
   * Test if the back button subscription is actually working by simulating the callback
   */
  testBackButtonSubscription() {
    console.log('[Dashboard] === Testing Back Button Subscription ===');

    if (!this.backButtonSub) {
      console.log('[Dashboard] ❌ No back button subscription to test');
      return;
    }

    console.log('[Dashboard] Testing back button subscription callback...');
    console.log('[Dashboard] Subscription object:', this.backButtonSub);
    console.log('[Dashboard] Subscription closed status:', this.backButtonSub.closed);

    // Simulate what would happen when the native back button is pressed
    // This will help us verify the logic works
    if (this.selectedMainSliderSection > this.minMainSlide) {
      const previousSlide = this.selectedMainSliderSection - 1;
      console.log('[Dashboard] Simulating back button press - would go from slide', this.selectedMainSliderSection, 'to slide', previousSlide);

      // Call the same logic that the subscription would call
      this.changeSlide(previousSlide, true);
    } else if (this.selectedMainSliderSection === this.minMainSlide) {
      console.log('[Dashboard] Simulating back button press - on first slide, would exit app');
    } else {
      console.log('[Dashboard] Simulating back button press - unexpected slide state:', this.selectedMainSliderSection);
    }
  }

  testController() {
    //this.labService.currentSelectedLab = this.userAuthService.currentUserComplete.labsComplete[1];
    //this.changeSlide(MainSliderSection.CONTROLLER);
  }

  /**
   * Perform complete dashboard reset on login - reload everything from scratch
   */
  private performCompleteReset(): void {
    console.log('[Dashboard] === PERFORMING COMPLETE DASHBOARD RESET ===');

    // Reset all dashboard state
    this.selectedMainSliderSection = MainSliderSection.LABS;
    this.transitioningSlideIndex = null;
    this.forceMainRenders = false;
    this.enableSideNav = false;
    this.hideNav = false;
    this.changingSlide = false;
    this.forceTransitionBackToContacts = false;
    this.currentlyManuallySliding = false;
    this.initialRender = true;  // Start with components hidden
    this.userNotificationContactRequest = null;

    // Clear any component references to force recreation
    this.contactsComponent = null;
    this.contactInfoComponent = null;
    this.gadgetComponent = null;
    this.gadgetComponentComponent = null;
    this.labComponent = null;
    this.dashboardSidenav = null;

    // Reset child components if they exist and have reset methods
    this.resetChildComponents();

    // Force Angular to detect changes and re-render everything
    this.cdr.detectChanges();

    // Force re-render of all components by toggling initialRender
    console.log('[Dashboard] Setting up setTimeout to show components in 100ms');
    setTimeout(() => {
      console.log('[Dashboard] === setTimeout callback executing ===');
      console.log('[Dashboard] initialRender before setting to false:', this.initialRender);
      this.initialRender = false;  // Show components
      console.log('[Dashboard] initialRender after setting to false:', this.initialRender);

      // Force another change detection cycle
      this.cdr.detectChanges();

      // Reset swiper to labs section if it exists
      if (this.swiper) {
        console.log('[Dashboard] Resetting swiper to labs section');
        this.swiper.slideTo(MainSliderSection.LABS, 0, false);
      }

      console.log('[Dashboard] === setTimeout callback completed ===');
    }, 100);

    console.log('[Dashboard] Complete dashboard reset finished');
  }

  /**
   * Reset all child components to their initial state
   */
  private resetChildComponents(): void {
    console.log('[Dashboard] Resetting child components');

    try {
      // Reset lab component if it exists and has resetComponent method
      if (this.labComponent && typeof this.labComponent.resetComponent === 'function') {
        console.log('[Dashboard] Resetting lab component');
        this.labComponent.resetComponent();
      }

      // Reset gadget component if it exists and has resetComponent method
      if (this.gadgetComponent && typeof this.gadgetComponent.resetComponent === 'function') {
        console.log('[Dashboard] Resetting gadget component');
        this.gadgetComponent.resetComponent();
      }

      // Reset gadget component component if it exists and has resetComponent method
      if (this.gadgetComponentComponent && typeof this.gadgetComponentComponent.resetComponent === 'function') {
        console.log('[Dashboard] Resetting gadget component component');
        this.gadgetComponentComponent.resetComponent();
      }

      // For other components, we rely on clearing references and re-rendering
      // since they don't have explicit reset methods
      console.log('[Dashboard] Child component references cleared - will be recreated on re-render');

    } catch (error) {
      console.error('[Dashboard] Error resetting child components:', error);
    }
  }

  /**
   * Clean up dashboard state on logout
   */
  private performLogoutCleanup(): void {
    console.log('[Dashboard] === PERFORMING LOGOUT CLEANUP ===');

    // Clear all state
    this.selectedMainSliderSection = MainSliderSection.LABS; // Reset to first slide
    this.transitioningSlideIndex = null;
    this.forceMainRenders = false;
    this.enableSideNav = false;
    this.hideNav = false;
    this.changingSlide = false;
    this.forceTransitionBackToContacts = false;
    this.currentlyManuallySliding = false;
    this.initialRender = false;
    this.userNotificationContactRequest = null;

    // Reset child components (clears editing state, etc.)
    this.resetChildComponents();

    // Reset swiper to first slide
    if (this.swiper) {
      console.log('[Dashboard] Resetting swiper to labs-list on logout');
      this.swiper.slideTo(MainSliderSection.LABS, 0, false);
    }

    // Clear component references
    this.contactsComponent = null;
    this.contactInfoComponent = null;

    console.log('[Dashboard] Logout cleanup finished');
  }

  //We handle back button stuff in our components
  handleBackButton() {
    console.log('[Dashboard] === Setting up back button handler ===');
    console.log('[Dashboard] Current slide:', this.selectedMainSliderSection);
    console.log('[Dashboard] Platform available:', !!this.platform);
    console.log('[Dashboard] Platform.backButton available:', !!this.platform?.backButton);

    if (!this.platform || !this.platform.backButton) {
      console.error('[Dashboard] ❌ Platform or backButton not available');
      return;
    }

    // Don't set up multiple subscriptions unless the existing one is closed
    if (this.backButtonSub && !this.backButtonSub.closed) {
      console.log('[Dashboard] Back button subscription already exists and is active, skipping setup');
      return;
    }

    // If we have a closed subscription, clean it up
    if (this.backButtonSub && this.backButtonSub.closed) {
      console.log('[Dashboard] Cleaning up closed back button subscription');
      this.backButtonSub = null;
    }

    // Ensure platform is ready before setting up back button handler
    this.platform.ready().then(() => {
      console.log('[Dashboard] Platform ready, setting up back button handler...');
      this.setupBackButtonHandler();
    }).catch(err => {
      console.error('[Dashboard] Platform ready failed, trying anyway:', err);
      this.setupBackButtonHandler();
    });
  }

  /**
   * Set up the actual back button handler
   */
  private setupBackButtonHandler() {
    console.log('[Dashboard] === setupBackButtonHandler called ===');
    console.log('[Dashboard] Current backButtonSub state:', this.backButtonSub);
    console.log('[Dashboard] Platform backButton available:', !!this.platform?.backButton);

    try {
      console.log('[Dashboard] Creating new back button subscription...');

      this.backButtonSub = this.platform.backButton.subscribeWithPriority(AndroidBackPriority.DASHBOARD, () => {
        console.log('[Dashboard] 🎯 BACK BUTTON PRESSED! 🎯');
        console.log('[Dashboard] Priority: 10001 (lowest priority)');
        console.log('[Dashboard] Current slide:', this.selectedMainSliderSection);
        console.log('[Dashboard] Min slide:', this.minMainSlide);
        console.log('[Dashboard] Max slide:', this.maxMainSlide);

        // Handle back button based on current component/slide
        switch (this.selectedMainSliderSection) {
          case MainSliderSection.LABS:
            // labs-list: do nothing
            console.log('[Dashboard] On labs-list, back button does nothing');
            break;

          case MainSliderSection.RESOURCES:
            // resources: do nothing
            console.log('[Dashboard] On resources, back button does nothing');
            break;

          case MainSliderSection.CONTACTS:
            // contacts: do nothing
            console.log('[Dashboard] On contacts, back button does nothing');
            break;

          case MainSliderSection.CONTACTINFO:
            // contact-info: go back to contacts
            console.log('[Dashboard] On contact-info, going back to contacts');
            this.changeSlide(MainSliderSection.CONTACTS, true);
            break;

          case MainSliderSection.LAB:
            // lab: go back to labs-list
            console.log('[Dashboard] On lab, going back to labs-list');
            this.changeSlide(MainSliderSection.LABS, true);
            break;

          case MainSliderSection.GADGET:
            // gadget: go back to lab
            console.log('[Dashboard] On gadget, going back to lab');
            this.changeSlide(MainSliderSection.LAB, true);
            break;

          case MainSliderSection.COMPONENT:
            // gadget-component: go back to gadget
            console.log('[Dashboard] On gadget-component, going back to gadget');
            this.changeSlide(MainSliderSection.GADGET, true);
            break;

          case MainSliderSection.CONTROLLER:
            // lab-controller: go back to lab
            console.log('[Dashboard] On lab-controller, going back to lab');
            this.changeSlide(MainSliderSection.LAB, true);
            break;

          case MainSliderSection.LABSETTINGS:
            // lab-settings: do nothing
            console.log('[Dashboard] On lab-settings, back button does nothing');
            break;

          case MainSliderSection.LABUSERS:
            // lab-user-list: do nothing
            console.log('[Dashboard] On lab-user-list, back button does nothing');
            break;

          default:
            console.log('[Dashboard] Unknown slide state:', this.selectedMainSliderSection);
            break;
        }
      });

      console.log('[Dashboard] ✅ Back button handler successfully set up!');
      console.log('[Dashboard] Subscription object:', this.backButtonSub);
    } catch (err) {
      console.error('[Dashboard] ❌ Error setting up back button handler:', err);

      // Alternative handler removed to prevent priority conflicts with dialogs
      console.log('[Dashboard] Alternative handler removed - using only main handler with priority 10001');
    }
  }

  setConfirmSlideChangeCallback(callback: () => Promise<boolean>) {
    this.confirmSlideChangeCallback = callback;
  }

  setHideNav(hideNav: boolean) {
    this.hideNav = hideNav;
  }

  ngAfterViewInit() {
    console.log('[Dashboard] === ngAfterViewInit called ===');
    console.log('[Dashboard] selectedMainSliderSection before auth subscription:', this.selectedMainSliderSection);

    this.navAnimationDisabled = false;

    this.userAuthService.fAuthStateChangedObservable.subscribe(() => {
      console.log('[Dashboard] Auth state changed, logged in:', this.userAuthService.loggedIn);
      if (this.userAuthService.loggedIn) {
        console.log('[Dashboard] User logged in - performing complete dashboard reset');
        this.performCompleteReset();
      }
      else {
        console.log('[Dashboard] User logged out - clearing dashboard state');
        this.performLogoutCleanup();
      }
      console.log('[Dashboard] selectedMainSliderSection after auth subscription:', this.selectedMainSliderSection);
    })

    //Give swiper time to initialize
    setTimeout(() => {
      console.log('[Dashboard] Setting up swiper...');
      let swiper: Swiper = this.swiperElementRef.nativeElement.swiper;
      this.swiper = swiper;
      console.log('[Dashboard] Swiper initialized:', !!this.swiper);

      // Minimal performance optimization - just enable hardware acceleration
      if (swiper && swiper.el) {
        swiper.el.style.transform = 'translateZ(0)';
      }

      //Prevent us from swiping to the left on the first slide
      this.setTouchMoveSettings();

      //Render all components so we can see them when changing slides
      swiper.on('sliderFirstMove', () => {
        // Only force renders if we are in the main section (indexes 0-4)
        if (this.swiper.activeIndex <= MainSliderSection.CODEBL) {
          this.ngZone.run(() => {
            this.forceMainRenders = true;
          });
        }
      });

      //Unrender components we aren't on now that slide has transitioned
      swiper.on('transitionEnd', () => {
        this.ngZone.run(() => {

          if (this.forceTransitionBackToContacts) {
            this.changeSlide(MainSliderSection.CONTACTS, true);
            this.forceTransitionBackToContacts = false;
            return;
          }

          if (this.selectedMainSliderSection != null) {
            this.stateService.triggerMainSliderSectionSettled(this.selectedMainSliderSection);
          }

        })

      });

      swiper.on('sliderMove', (event) => {
        this.currentlyManuallySliding = true;
        if (this.selectedMainSliderSection >= this.minMainSlide && this.selectedMainSliderSection <= this.maxMainSlide) {
          this.forceMainRenders = true;
        }
      });

      swiper.on('slideChange', () => {
        console.log('[Dashboard] === slideChange event fired ===');
        console.log('[Dashboard] Swiper active index:', this.swiper.activeIndex);
        console.log('[Dashboard] Previous selectedMainSliderSection:', this.selectedMainSliderSection);

        this.ngZone.run(() => {
          this.changingSlide = true;

          this.transitioningSlideIndex = this.selectedMainSliderSection;

          //Prevent user from swiping past the main slides
          if (this.swiper.activeIndex - this.selectedMainSliderSection > this.maxMainSlide && this.currentlyManuallySliding) {
            this.forceTransitionBackToContacts = true;
            this.changingSlide = false;
            return;
          }

          //Prevent user from swiping during swipe
          this.swiper.allowTouchMove = false;

          let mainSliderSection: MainSliderSection = this.swiper.activeIndex;

          this.hideNav = false;

          //Happens once transition is finished
          setTimeout(() => {
            // Don't hide lab component immediately - allow it to render
            if (mainSliderSection !== MainSliderSection.LAB) {
              this.forceMainRenders = false;
            }

            //Set values with slide we just changed to. We need this one and one above
            this.setTouchMoveSettings();

            // Keep programmatic navigation enabled but disable touch/swipe gestures
            this.swiper.allowTouchMove = false;

            this.changingSlide = false;
            this.currentlyManuallySliding = false;
            this.transitioningSlideIndex = null;

          }, transitionSpeed)

          // Store the previous slide before updating
          const previousSlide = this.selectedMainSliderSection;

          this.selectedMainSliderSection = mainSliderSection;
          console.log('[Dashboard] Updated selectedMainSliderSection to:', this.selectedMainSliderSection);

          // Call component lifecycle methods now that the slide has changed and component is rendered
          if (mainSliderSection == MainSliderSection.COMPONENT) {
            console.log('[Dashboard] Component slide is now active, calling onSlideEnter');
            this.callGadgetComponentMethod('onSlideEnter');
          } else if (previousSlide == MainSliderSection.COMPONENT) {
            console.log('[Dashboard] Exiting component slide, calling onSlideExit');
            this.callGadgetComponentMethod('onSlideExit');
          }

        });
      });

      console.log('[Dashboard] Swiper setup complete');
    }, 300)
  }


  //Called when we manually change a slide. All other data needs to be in swipers slideChange event
  async changeSlide(mainSliderSection: MainSliderSection, force: boolean = false) {
    console.log('[Dashboard] === changeSlide called ===');
    console.log('[Dashboard] Requested slide:', mainSliderSection);
    console.log('[Dashboard] Current slide:', this.selectedMainSliderSection);
    console.log('[Dashboard] Force:', force);

    if (mainSliderSection == MainSliderSection.CONTACTINFO && Capacitor.getPlatform() == "web") {
      this.loadContactInfoDialog();
      this.userNotificationContactRequest = null;
      return;
    }

    if (this.confirmSlideChangeCallback != null) {
      let result = await this.confirmSlideChangeCallback();
      if (result != true) {
        console.log('[Dashboard] Slide change blocked by callback');
        return;
      }
    }
    if (this.changingSlide == true && force == false) {
      console.log('[Dashboard] Slide change blocked - already changing');
      return;
    }

    //Reset this - but preserve userNotificationContactRequest if we're going to CONTACTINFO
    if (mainSliderSection !== MainSliderSection.CONTACTINFO) {
      this.userNotificationContactRequest = null;
    } else {
      console.log("Preserving userNotificationContactRequest for CONTACTINFO:", this.userNotificationContactRequest);
    }
    this.confirmSlideChangeCallback = null;

    this.enableSlider();

    console.log("current selected lab", this.labService.currentSelectedLab)

    // Pre-render the target slide to prevent UI lag during transition
    // This ensures the component is "display: block" and painted BEFORE we try to animate to it
    this.transitioningSlideIndex = mainSliderSection;

    // Simple preloading without waiting - let images load naturally
    if (mainSliderSection == MainSliderSection.COMPONENT) {
      console.log('[Dashboard] Entering component slide, will call onSlideEnter in slideChange event');
      this.preloadComponentData();
    }
    else if (mainSliderSection == MainSliderSection.GADGET) {
      this.preloadGadgetData();
    }
    else if (mainSliderSection == MainSliderSection.LAB) {
      this.preloadLabData();
    }

    // Check user access when navigating to lab-related views
    if (mainSliderSection == MainSliderSection.LAB ||
      mainSliderSection == MainSliderSection.GADGET ||
      mainSliderSection == MainSliderSection.COMPONENT ||
      mainSliderSection == MainSliderSection.CONTROLLER ||
      mainSliderSection == MainSliderSection.LABSETTINGS ||
      mainSliderSection == MainSliderSection.LABUSERS) {
      this.checkUserAccessToCurrentLab();
    }

    // Minimal delay to allow preloading to start
    if (mainSliderSection == MainSliderSection.COMPONENT || mainSliderSection == MainSliderSection.GADGET || mainSliderSection == MainSliderSection.LAB) {
      await new Promise(resolve => setTimeout(resolve, 10)); // Just 10ms to start preloading
    }

    let swiperLag = 100;
    if (this.capacitorPlatform == "web") {
      //swiperLag = 0;
    }


    setTimeout(() => {
      console.log("transitionSpeed", transitionSpeed)
      //If we are on a lab page, we need to enable the side nav
      if (mainSliderSection == MainSliderSection.LAB || mainSliderSection == MainSliderSection.GADGET || mainSliderSection == MainSliderSection.COMPONENT || mainSliderSection == MainSliderSection.CONTROLLER || mainSliderSection == MainSliderSection.LABSETTINGS || mainSliderSection == MainSliderSection.LABUSERS) {
        this.enableSideNav = true;
      }
      else {
        this.enableSideNav = false;
        this.labService.leaveLab();

      }
      console.log("enable sidenav", this.enableSideNav)
      console.log('[Dashboard] Executing slide change to:', mainSliderSection);
      this.swiper?.slideTo(mainSliderSection, transitionSpeed, true);

    }, swiperLag)
  }

  loadContactInfoDialog() {
    let dialogRef = this.matDialog.open(ContactInfoComponent, {
      height: '600px',
      width: '370px',
      maxWidth: "93vw",
      data: {
        userNotificationContactRequest: this.userNotificationContactRequest
      },
      autoFocus: false,

    });

    // Pass the dialogRef to the component after it's created
    if (dialogRef.componentInstance) {
      dialogRef.componentInstance.setDialogRef(dialogRef);
    }

    dialogRef.afterClosed().subscribe(() => {
      this.changeSlide(MainSliderSection.CONTACTS);

    });
  }

  //Call from children to enable/disable sliding
  enableSlider() {
    if (this.swiper) {
      // Keep programmatic navigation enabled but disable touch/swipe gestures
      this.swiper.allowSlidePrev = true;
      this.swiper.allowSlideNext = true;
    }

  }

  //Prevent user from manually swiping - only allow programmatic navigation
  setTouchMoveSettings() {
    // Keep programmatic navigation enabled but disable touch/swipe gestures
    this.swiper.allowSlidePrev = true;
    this.swiper.allowSlideNext = true;

    // Minimal swiper optimization
    if (this.swiper) {
      this.swiper.updateSize();
    }
  }

  disableSlider() {
    this.swiper.allowSlideNext = false;
    this.swiper.allowSlidePrev = false;
  }

  enableSelectingContactMode() {
    if (this.changingSlide == true) {
      return;
    }
    this.changeSlide(MainSliderSection.CONTACTS);
    this.disableSlider();

    setTimeout(() => {
      this.hideNav = true;
      this.contactsComponent.contactsMode = this.contactsComponent.ContactsMode.SELECT;
    }, 10)
  }

  selectedContact(selectedContact: SelectedContact) {
    if (this.changingSlide == true) {
      return;
    }

    if (selectedContact.contactsMode == ContactsMode.REGULAR) {
      this.sendUserContactToContactInfo(selectedContact.userContact);
    }
    else if (selectedContact.contactsMode == ContactsMode.SELECT) {
      if (selectedContact.userContact) {
        let loading = presentLoading(this.matDialog);

      }
      else {
        this.changeSlide(MainSliderSection.LABS);

      }
    }

  }

  sendUserNotificationToContactInfo(contactRequestUserNotification: UserNotification) {
    if (this.changingSlide == true) {
      return;
    }
    console.log("go to contact info", contactRequestUserNotification)
    console.log("Platform:", Capacitor.getPlatform())
    if ((contactRequestUserNotification.dynamicSettings as UserNotificationContactRequest)?.userContactRequestId == null) {
      presentSnackBar("The contact request has been deleted", SnackBarColor.DANGER, this.matSnackBar);
    }
    else {

      this.userNotificationContactRequest = contactRequestUserNotification.dynamicSettings;
      console.log("Set userNotificationContactRequest:", this.userNotificationContactRequest)
      this.changeSlide(MainSliderSection.CONTACTINFO);

    }
  }

  sendUserContactToContactInfo(userContact: UserContact) {
    if (this.changingSlide == true) {
      return;
    }
    this.changeSlide(MainSliderSection.CONTACTINFO);

    setTimeout(() => {
      this.contactInfoComponent.setUpdatingContact(userContact);
    }, 10)
  }

  onContactRequestProcessed() {
    // Reset the contact request after it has been processed by the component
    console.log("Contact request processed, resetting userNotificationContactRequest");
    this.userNotificationContactRequest = null;
  }

  // Call a method on the gadget component, with fallback if ViewChild is null
  private callGadgetComponentMethod(methodName: string) {
    console.log(`[Dashboard] Attempting to call ${methodName} on gadget component`);

    // Try using the ViewChild first
    if (this.gadgetComponentComponent && typeof (this.gadgetComponentComponent as any)[methodName] === 'function') {
      console.log(`[Dashboard] Calling ${methodName} via ViewChild`);
      (this.gadgetComponentComponent as any)[methodName]();
      return;
    }

    // Fallback: try to get the component from DOM and call the method
    console.log(`[Dashboard] ViewChild not available, trying DOM fallback for ${methodName}`);
    const element = document.querySelector('gadget-component');
    if (element) {
      // Try to get the component instance from the DOM
      const ngContext = (element as any).__ngContext__;
      let componentInstance = null;

      if (Array.isArray(ngContext)) {
        componentInstance = ngContext.find((item: any) => item && item.constructor && item.constructor.name === 'GadgetComponentComponent');
      } else if (ngContext && ngContext.constructor && ngContext.constructor.name === 'GadgetComponentComponent') {
        componentInstance = ngContext;
      }

      if (componentInstance && typeof componentInstance[methodName] === 'function') {
        console.log(`[Dashboard] Calling ${methodName} via DOM component instance`);
        componentInstance[methodName]();
        // Update the ViewChild reference for future calls
        this.gadgetComponentComponent = componentInstance;
      } else {
        console.error(`[Dashboard] Could not find component instance or method ${methodName} in DOM`);
      }
    } else {
      console.error(`[Dashboard] Could not find gadget-component element in DOM`);
    }
  }

  // Initialize the gadget component component reference
  private initializeGadgetComponentComponent() {
    console.log('[Dashboard] Initializing gadgetComponentComponent ViewChild');
    console.log('[Dashboard] Current gadgetComponentComponent:', this.gadgetComponentComponent);
    console.log('[Dashboard] Current selected component:', this.labService.currentSelectedComponent);
    console.log('[Dashboard] Current selectedMainSliderSection:', this.selectedMainSliderSection);
    console.log('[Dashboard] Current transitioningSlideIndex:', this.transitioningSlideIndex);

    // Ensure we have a selected component before trying to access the ViewChild
    if (!this.labService.currentSelectedComponent) {
      console.error('[Dashboard] No selected component available, cannot initialize gadget component');
      return;
    }

    // Check if component should be visible
    const shouldBeVisible = !(this.selectedMainSliderSection != MainSliderSection.COMPONENT && this.transitioningSlideIndex != MainSliderSection.COMPONENT);
    console.log('[Dashboard] Component should be visible:', shouldBeVisible);

    // Force change detection to ensure ViewChild is available
    this.cdr.detectChanges();

    console.log('[Dashboard] After detectChanges, gadgetComponentComponent:', this.gadgetComponentComponent);

    if (!this.gadgetComponentComponent) {
      console.error('[Dashboard] gadgetComponentComponent is still null after detectChanges');
      // Try to get it from the DOM directly
      const element = document.querySelector('gadget-component');
      console.log('[Dashboard] Found gadget-component element in DOM:', !!element);

      if (element) {
        // Try to get the Angular component instance from the DOM element
        const componentInstance = (element as any).__ngContext__?.[0];
        console.log('[Dashboard] Found component instance from DOM:', !!componentInstance);

        if (componentInstance) {
          this.gadgetComponentComponent = componentInstance;
          console.log('[Dashboard] Set gadgetComponentComponent from DOM element');
        } else {
          // Try alternative method to get component instance
          const ngContext = (element as any).__ngContext__;
          console.log('[Dashboard] ngContext type:', typeof ngContext, 'isArray:', Array.isArray(ngContext));

          if (Array.isArray(ngContext)) {
            const ngComponentRef = ngContext.find((item: any) => item && item.constructor && item.constructor.name === 'GadgetComponentComponent');
            console.log('[Dashboard] Found component via alternative method:', !!ngComponentRef);

            if (ngComponentRef) {
              this.gadgetComponentComponent = ngComponentRef;
              console.log('[Dashboard] Set gadgetComponentComponent from alternative method');
            }
          } else if (ngContext) {
            // If ngContext is not an array, try to find the component in it
            console.log('[Dashboard] ngContext is not an array, trying direct access');
            if (ngContext.constructor && ngContext.constructor.name === 'GadgetComponentComponent') {
              this.gadgetComponentComponent = ngContext;
              console.log('[Dashboard] Set gadgetComponentComponent from direct ngContext access');
            }
          }

          // Try one more approach - get the component from the element's parent
          if (!this.gadgetComponentComponent) {
            console.log('[Dashboard] Trying to get component from element parent');
            const parentElement = element.parentElement;
            if (parentElement) {
              const parentNgContext = (parentElement as any).__ngContext__;
              console.log('[Dashboard] Parent ngContext type:', typeof parentNgContext, 'isArray:', Array.isArray(parentNgContext));

              if (Array.isArray(parentNgContext)) {
                const parentComponent = parentNgContext.find((item: any) => item && item.constructor && item.constructor.name === 'GadgetComponentComponent');
                if (parentComponent) {
                  this.gadgetComponentComponent = parentComponent;
                  console.log('[Dashboard] Set gadgetComponentComponent from parent element');
                }
              }
            }
          }
        }
      }

      // Try again after a short delay
      setTimeout(() => {
        this.cdr.detectChanges();
        console.log('[Dashboard] After timeout and detectChanges, gadgetComponentComponent:', this.gadgetComponentComponent);

        // If still null, try one more time with a longer delay
        if (!this.gadgetComponentComponent) {
          setTimeout(() => {
            this.cdr.detectChanges();
            console.log('[Dashboard] Final attempt - gadgetComponentComponent:', this.gadgetComponentComponent);

            // If still null, try to force the component to be available
            if (!this.gadgetComponentComponent) {
              console.log('[Dashboard] Component still null, trying to force availability');
              // Try to trigger the component's ngOnInit or similar
              const element = document.querySelector('gadget-component');
              if (element) {
                // Dispatch a custom event to trigger component initialization
                element.dispatchEvent(new CustomEvent('force-init'));
                this.cdr.detectChanges();
                console.log('[Dashboard] After force-init - gadgetComponentComponent:', this.gadgetComponentComponent);
              }
            }
          }, 50);
        }
      }, 10);
    }
  }

  // Preload component data before transition
  private preloadComponentData() {
    try {
      // Trigger data loading in background
      this.gadgetComponentComponent.preloadData();
      // Note: onSlideEnter is already called in changeSlide method
    } catch (error) {
      console.warn('Error preloading component data:', error);
    }
  }


  // Preload gadget data before transition
  private preloadGadgetData() {
    try {
      if (this.gadgetComponent) {
        // Trigger data loading in background
        this.gadgetComponent.preloadData();
      }
    } catch (error) {
      console.warn('Error preloading gadget data:', error);
    }
  }

  // Preload lab data before transition
  private preloadLabData() {
    try {
      console.log("preload lab data 0")
      if (this.labComponent) {
        // Lab component now automatically watches for service changes
        console.log("preload lab data 1 - using automatic watchers")
      }
    } catch (error) {
      console.warn('Error preloading lab data:', error);
    }
  }

  // iOS-specific optimizations to prevent visual glitches
  private addIOSOptimizations() {
    try {
      // Add CSS to body to prevent iOS-specific visual issues
      if (typeof document !== 'undefined') {
        const style = document.createElement('style');
        style.textContent = `
          .lazy-image-wrapper {
            -webkit-transform: translateZ(0);
            transform: translateZ(0);
            -webkit-backface-visibility: hidden;
            backface-visibility: hidden;
          }
          .lazy-image, .lazy-image-placeholder, .lazy-image-error {
            -webkit-transform: translateZ(0);
            transform: translateZ(0);
            -webkit-backface-visibility: hidden;
            backface-visibility: hidden;
          }
        `;
        document.head.appendChild(style);
      }
    } catch (error) {
      console.warn('Error adding iOS optimizations:', error);
    }
  }

  /**
   * Re-establish the dashboard's back button handler after dialog closure
   * This ensures the handler is active and working properly
   */
  public reestablishBackButtonHandler() {
    console.log('[Dashboard] === Re-establishing back button handler ===');

    // Clean up existing subscription if it exists
    if (this.backButtonSub) {
      console.log('[Dashboard] Cleaning up existing back button subscription');
      try {
        this.backButtonSub.unsubscribe();
      } catch (err) {
        console.warn('[Dashboard] Error unsubscribing from back button:', err);
      }
      this.backButtonSub = null;
    }

    // Force a small delay to ensure cleanup is complete
    setTimeout(() => {
      console.log('[Dashboard] Setting up new back button handler...');
      this.handleBackButton();
    }, 50);

    console.log('[Dashboard] ✅ Back button handler re-establishment initiated');
  }

  /**
   * Force re-establishment of back button handler for testing
   */
  public forceReestablishBackButton() {
    console.log('[Dashboard] === Force re-establishing back button handler ===');
    this.reestablishBackButtonHandler();
  }

  /**
   * Set up a global back button listener to debug if the native back button is being detected
   * NOTE: This is disabled because it has no priority and interferes with other handlers
   */
  private setupGlobalBackButtonListener() {
    console.log('[Dashboard] === Global back button listener disabled ===');
    console.log('[Dashboard] Using only prioritized handlers to avoid conflicts');

    // Global back button listener is disabled because it has no priority (defaults to 0)
    // and was intercepting all back button presses before other handlers could process them
  }

  /**
   * Try using Capacitor App plugin for back button handling as a fallback
   * NOTE: This is disabled because it conflicts with Ionic's priority system
   */
  async tryCapacitorAppBackButton() {
    console.log('[Dashboard] === Capacitor App Plugin disabled ===');
    console.log('[Dashboard] Using only Ionic Platform service with proper priorities');

    // Capacitor App plugin is disabled because it doesn't respect Ionic's priority system
    // and was causing conflicts with dialog back button handlers
    // The main Platform service handler with priority 1000 is sufficient
  }


}
